import {useLocation, useNavigate} from "react-router-dom";
import {
  Alert,
  Anchor,
  Box,
  Center,
  CloseButton,
  Container,
  Group,
  LoadingOverlay,
  Paper,
  Space,
  Title
} from "@mantine/core";
import {
  adjustToTimeZone,
  getObjectId,
  getRequiredObjectId,
  OnSiteTimeReportCriteria,
  useDownloadOnSiteTimeReport
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useEffect} from "react";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {
  OnSiteTimeReportForm,
  OnSiteTimeReportFormValues
} from "../../components/form/OnSiteTimeReportForm/OnSiteTimeReportForm";
import {IconAlertCircle} from "@tabler/icons-react";
import {useScrollIntoView} from "@mantine/hooks";

type Props = {
  formDefaults?: Partial<OnSiteTimeReportFormValues>
}

export const OnSiteTimeReportFormPageRoute = () => {
  const {state} = useLocation();

  const formDefaults = state?.formDefaults;

  return (
    <OnSiteTimeReportFormPage formDefaults={formDefaults}/>
  );
};

export const OnSiteTimeReportFormPage = (props: Props) => {
    const {formDefaults} = props;

    const navigate = useNavigate();
    const {t} = useTranslation(["common"]);
    const {regionId, baseQueryApi} = useApp()

    const {scrollIntoView: scrollErrorIntoView, targetRef: errorTargetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {scrollIntoView, targetRef} = useScrollIntoView<HTMLDivElement>({
      offset: 120,
      duration: 0,
    });

    const {
      downloadOnSiteTimeReport,
      report,
      error,
      loading
    } = useDownloadOnSiteTimeReport({baseQueryApi})

    const onFormSubmit = useCallback((formValues: OnSiteTimeReportFormValues) => {
      if (regionId) {
        const criteria: OnSiteTimeReportCriteria = {
          regionId: regionId,
          dttm: adjustToTimeZone(formValues.date).getTime(),
          operatorId: getRequiredObjectId(formValues.operator),
          siteId: getObjectId(formValues.site),
          siteCompanyId: getObjectId(formValues.siteCompany)
        }

        downloadOnSiteTimeReport(criteria)
      }
    }, [regionId, downloadOnSiteTimeReport])

    useEffect(() => {
      if (report) {
        scrollIntoView({alignment: 'start'})
      }
    }, [report, scrollIntoView]);

    useEffect(() => {
      if (error) {
        scrollErrorIntoView({alignment: 'start'})
      }
    }, [error, scrollErrorIntoView]);

    return (
      <ThemedPage>
        {(classes) => (
          <Container className={classes.container} fluid={true} py="md">
            <Center>
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {t("common.page.report.onsitetime.title", "On Site Time Report", {ns: "common"})}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                {error &&
                  <>
                    <Alert ref={errorTargetRef} icon={<IconAlertCircle size="1rem"/>} title="Error" color="red">
                      {error?.data?.detail ?? t("common.error", "Error", {ns: "common"})}
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                {report &&
                  <>
                    <Alert ref={targetRef} icon={<IconAlertCircle size="1rem"/>} title="Success" color="green">
                      <Anchor href={report} target="_blank">
                        {t("common.action.download", "Download")}
                      </Anchor>
                    </Alert>
                    <Space h="md"/>
                  </>
                }
                <Box pos="relative">
                  <LoadingOverlay visible={loading}/>
                  <OnSiteTimeReportForm
                    defaultValues={{
                      ...(formDefaults ?? {}),
                    }}
                    options={{
                      regionId: regionId,
                    }}
                    submitting={loading}
                    onSubmit={onFormSubmit}/>
                </Box>
              </Paper>
            </Center>
          </Container>
        )}
      </ThemedPage>
    );
  }
;

